// Who for PAM (SSH): console users by email, and directory groups across
// every AD and Entra directory the tenant has. Both lists are read once per
// page load and filtered here as the admin types.
import { getPlatformUserList } from "../../../../service/tenantService";
import { listDirectories, directoryKind } from "../../../../service/appPolicyService";
import { dispatchGetAllGroupsByDomain } from "../../../../utils/apiservice";
import { DOMAIN_ID, ORG_ID } from "../../../../constants";

const needleOf = (q) => String(q || "").trim().toLowerCase();

/* ---------------------------------------------------------- console users */

let usersPromise = null;

/** Console users with an email: [{ userId, email }]. Cached; a failure is retried next time. */
export function loadConsoleUsers() {
  if (!usersPromise) {
    usersPromise = new Promise((resolve, reject) =>
      getPlatformUserList({
        payload: { page: 1, pageSize: 500 },
        onSuccess: (res) => resolve((res?.data || []).filter((u) => u?.email).map((u) => ({ userId: u.userId, email: u.email }))),
        onFailure: () => reject(new Error("Couldn't load console users.")),
      }),
    ).catch((e) => {
      usersPromise = null;
      throw e;
    });
  }
  return usersPromise;
}

export const searchConsoleUsers = (q) =>
  loadConsoleUsers().then((users) => {
    const n = needleOf(q);
    return users.filter((u) => !n || u.email.toLowerCase().includes(n)).slice(0, 100).map((u) => ({ value: u.email }));
  });

/* ------------------------------------------------------- directory groups */

/**
 * One list of groups from several directories: [{ directory, groups: [name] }]
 * -> [{ value, label, sub }], one entry per name (case-insensitive), sorted,
 * sub naming the directories that have it ("corp (Active Directory)").
 * The policy stores the name; the SSH engine matches names, not directories.
 */
export function mergeDirectoryGroups(perDirectory) {
  const byKey = new Map();
  (perDirectory || []).forEach(({ directory, groups }) => {
    const where = directory ? `${directory.directoryName || directory.domainName || `#${directory.id}`} (${directoryKind(directory)})` : "";
    (groups || []).forEach((g) => {
      const name = String((typeof g === "string" ? g : g?.groupName || g?.name) || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      const hit = byKey.get(key) || { value: name, dirs: [] };
      if (where && !hit.dirs.includes(where)) hit.dirs.push(where);
      byKey.set(key, hit);
    });
  });
  return [...byKey.values()]
    .sort((a, b) => a.value.localeCompare(b.value))
    .map((g) => ({ value: g.value, label: g.value, sub: g.dirs.join(", ") }));
}

// GetAllGroupsByDomain shares one AbortController per endpoint (an aborted
// call never settles), so the directories are read one after another.
const groupsOf = (dir) =>
  new Promise((resolve) =>
    dispatchGetAllGroupsByDomain({
      payload: { tenantId: DOMAIN_ID(), orgId: ORG_ID(), adId: dir.id, pageNo: 1, pageSize: 500, filter: "" },
      onSuccess: (res) => resolve({ directory: dir, groups: res?.groups || [], ok: true }),
      onFailure: () => resolve({ directory: dir, groups: [], ok: false }),
      onError: () => resolve({ directory: dir, groups: [], ok: false }),
    }),
  );

let groupsPromise = null;

/** Every directory's groups, merged. Cached; a failure is retried next time. */
export function loadDirectoryGroups() {
  if (!groupsPromise) {
    groupsPromise = listDirectories()
      .then(async (dirs) => {
        const results = [];
        for (const dir of dirs) {
          // eslint-disable-next-line no-await-in-loop
          results.push(await groupsOf(dir));
        }
        if (dirs.length && !results.some((r) => r.ok)) throw new Error("Couldn't load directory groups.");
        return mergeDirectoryGroups(results);
      })
      .catch((e) => {
        groupsPromise = null;
        throw new Error(e?.message && !e?.text ? e.message : "Couldn't load directory groups.");
      });
  }
  return groupsPromise;
}

export const searchDirectoryGroups = (q) =>
  loadDirectoryGroups().then((groups) => {
    const n = needleOf(q);
    return groups.filter((g) => !n || g.value.toLowerCase().includes(n)).slice(0, 100);
  });

/** Test hook: forget the cached lists. */
export function resetDirectoryCache() {
  usersPromise = null;
  groupsPromise = null;
}
